// Genera el HTML del Informe SyB 7 · Riesgos Naturales
// Se abre en una ventana nueva del navegador y se puede guardar como PDF
// desde el diálogo Imprimir. Usa Leaflet + WMS oficiales embebidos.
//
// ESTRUCTURA (v2 · un mapa por sección):
//   3. Evaluación por tipo de amenaza
//     3.1 Sismicidad          — tabla + mapa propio (NCSE-02 · PGA 475) + leyendas dinámicas
//     3.2 Incendios forestales — tabla + mapa propio (ZARI / Perill / risc greu) + leyendas
//     3.3 Meteorología         — tabla + enlace AEMET Meteoalerta
//     3.4 Estabilidad geológica — tabla + mapa propio (IGME BDMoves · ICGC · Erupciones) + leyendas
//
// LEYENDAS: se muestran solo las de las capas ACTIVAS en cada mini-mapa.
// El listener overlayadd/overlayremove las regenera cuando el usuario marca
// o desmarca capas en el control.

import { getSyb7Sources } from './syb7Sources'

function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]))
}

// ── Configuración canónica de leyendas por capa ────────────────────────
// mode = 'wide' | 'split' (imagen alta dividida en tiras) | 'clip' (recorte de degradado)
// Estos formatos y tamaños son los que ya funcionaban en el mapa preview
// (NaturalRisksMap.js) y se replican aquí para consistencia visual.
const LEGEND_META = {
  ign_ncse02: {
    title: 'Sismicidad NCSE-02 (IGN)',
    note: 'Aceleración sísmica básica <em>a<sub>b</sub></em> (<em>g</em>) según Anejo 1 NCSE-02. &lt;0,04 bajo · 0,04-0,08 moderado · 0,08-0,16 significativo · &gt;0,16 alto.',
    url: 'https://www.ign.es/wms-inspire/geofisica?service=WMS&request=GetLegendGraphic&format=image/png&layer=HazardArea2002.NCSE-02&sld_version=1.1.0',
    mode: 'wide',
  },
  ign_pga475: {
    title: 'PGA 475a (IGN 2015)',
    note: 'Aceleración pico del terreno · retorno 475 años (10 % excedencia en 50 años). Escala en <em>g</em>.',
    url: 'https://www.ign.es/wms-inspire/geofisica?service=WMS&request=GetLegendGraphic&format=image/png&layer=HazardArea2015.PGA475_p&sld_version=1.1.0',
    mode: 'wide',
  },
  pcivil_perill: {
    title: 'Perill incendi (Generalitat)',
    note: 'Nivel anual Protecció Civil Catalunya · Baix · Moderat · Alt · Molt alt · Extrem. Escala municipal.',
    url: 'https://pcivil.icgc.cat/ogc/geoservei?map=/opt/idec/dades/pcivil/risc_incendis.map&service=WMS&version=1.3.0&request=GetLegendGraphic&format=image/png&layer=infocat_perill&sld_version=1.1.0',
    mode: 'wide',
  },
  pcivil_greu: {
    title: 'Zones risc greu incendi',
    note: 'Municipios de alto riesgo de incendio forestal (Decret 64/1995). Aplica el Pla ALFA de Protecció Civil.',
    url: 'https://pcivil.icgc.cat/ogc/geoservei?map=/opt/idec/dades/pcivil/risc_incendis.map&service=WMS&version=1.3.0&request=GetLegendGraphic&format=image/png&layer=zones_risc_greu_incendis&sld_version=1.1.0',
    mode: 'wide',
  },
  icgc_riscs: {
    title: 'Riscos geològics (ICGC · Catalunya)',
    note: 'Mapa de prevención 1:25 000 · inventario de fenómenos, deslizamientos y flujos torrenciales.',
    url: 'https://geoserveis.icgc.cat/servei/catalunya/riscos-geologics/wms/service?service=WMS&version=1.3.0&request=GetLegendGraphic&format=image/png&layer=G6FIA_PN&sld_version=1.1.0',
    mode: 'wide',
  },
  idecan_zari: {
    title: 'ZARI Canarias (Gobierno Canarias)',
    note: 'Zonas de Alto Riesgo de Incendio Forestal · Decreto 100/2002 · IDECanarias.',
    url: 'https://idecan2.grafcan.es/ServicioWMS/ZARI?service=WMS&version=1.3.0&request=GetLegendGraphic&format=image/png&layer=ZARI&sld_version=1.1.0',
    mode: 'wide',
  },
  ign_erupciones: {
    title: 'Erupciones históricas (IGN)',
    note: 'Registro histórico de erupciones volcánicas · Red de Vigilancia Volcánica del IGN. Canarias.',
    url: 'https://wms-volcanologia.ign.es/volcanologia?service=WMS&version=1.3.0&request=GetLegendGraphic&format=image/png&layer=erupciones&sld_version=1.1.0',
    mode: 'wide',
  },
  igme_bdmoves: {
    title: 'Movimientos del terreno (IGME BDMoves)',
    note: 'Base nacional de deslizamientos, subsidencias, colapsos, expansividad. Cobertura toda España (incluye Canarias).',
    url: 'https://mapas.igme.es/gis/services/BasesDatos/IGME_BDMoves_ES/MapServer/WMSServer?service=WMS&version=1.3.0&request=GetLegendGraphic&format=image/png&layer=0&sld_version=1.1.0',
    mode: 'wide',
  },
}

// ── Caracterización climática por clasificación Köppen-Geiger ────────
// Descripción del clima local a partir del código Köppen que el usuario
// haya seleccionado en Datos Proyecto. Datos climatológicos orientativos
// (rangos típicos peninsulares/canarios), no reemplaza los datos AEMET
// específicos de la estación cuando estén disponibles.
const KOPPEN_INFO = {
  'A. Equatorial':      { label: 'Tropical / Ecuatorial',        temp: '22-27 °C', prec: '> 1.500 mm', descripcion: 'Clima cálido y muy húmedo, sin estación seca marcada.' },
  'B. Arid':            { label: 'Árido (BWh / BSh / BSk)',      temp: '18-24 °C', prec: '100-400 mm',  descripcion: 'Clima seco con evapotranspiración superior a la precipitación. Estrés hídrico crónico.' },
  'C. Warm Temperate':  { label: 'Templado cálido (Cs/Cf)',       temp: '13-18 °C', prec: '400-900 mm',  descripcion: 'Clima templado con inviernos suaves y veranos cálidos a calurosos. Mediterráneo dominante en costa española.' },
  'D. Snow':            { label: 'Continental / con nieve (Ds/Df)', temp: '5-13 °C', prec: '500-1.200 mm', descripcion: 'Clima con inviernos fríos (temperatura media del mes más frío por debajo de 0 °C). Zonas de montaña interior.' },
  'E. Polar':           { label: 'Polar / Alpino (ET/EF)',        temp: '< 5 °C',   prec: 'variable',    descripcion: 'Clima frío alpino, temperatura media del mes más cálido inferior a 10 °C. Alta montaña.' },
}

// ── Riesgos meteorológicos característicos por CCAA ──────────────────
const METEO_RIESGOS_CCAA = {
  'CANARIAS': [
    { fenomeno: 'Calima (polvo sahariano)',   frecuencia: 'Alta',      descripcion: 'Advección de aire y polvo desde el Sáhara — reduce visibilidad, degrada la calidad del aire (PM10 > 200 µg/m³) y ensucia superficies vidriadas y captadores FV.' },
    { fenomeno: 'Vientos alisios intensos',   frecuencia: 'Alta',      descripcion: 'Régimen NE dominante de mayo a septiembre; rachas puntuales > 70 km/h en cotas altas y crestas.' },
    { fenomeno: 'Temporales tropicales',      frecuencia: 'Baja',      descripcion: 'Depresiones subtropicales ocasionales (huracán Delta 2005, tormenta Hermine 2022, huracán Leslie 2018). Precipitaciones torrenciales.' },
    { fenomeno: 'Sequía prolongada',          frecuencia: 'Alta',      descripcion: 'Precipitación anual < 300 mm en costa sur — restricciones hídricas periódicas.' },
    { fenomeno: 'Ola de calor',               frecuencia: 'Media',     descripcion: 'Episodios con temperaturas > 35 °C en cumbres bajas y zonas de sotavento cuando entra advección sahariana.' },
  ],
  'CATALUÑA': [
    { fenomeno: 'Ola de calor',               frecuencia: 'Alta',      descripcion: 'Episodios de junio a septiembre con máximas > 35 °C en depresión central (Lleida) y costa. Fenómeno agravado por cambio climático.' },
    { fenomeno: 'Tormentas convectivas / granizo', frecuencia: 'Alta', descripcion: 'Episodios severos en primavera y otoño, especialmente en interior. Alta capacidad de daño en cubiertas, FV y jardinería.' },
    { fenomeno: 'Precipitación torrencial',   frecuencia: 'Media',     descripcion: 'Episodios de gota fría en Levante — precipitaciones > 100 mm/día que sobrepasan capacidad de drenaje urbano.' },
    { fenomeno: 'Nevadas',                    frecuencia: 'Media',     descripcion: 'Pirineos y Pre-Pirineos: nevadas regulares en invierno. Media montaña ocasional. Impacto en circulación y en cubiertas.' },
    { fenomeno: 'Vent de garbí / mestral',    frecuencia: 'Media',     descripcion: 'Vientos regionales del NW (mestral) con rachas > 80 km/h en el delta del Ebro y Terres de l\'Ebre.' },
  ],
}

// ── Enlace AdaptaCCa (MITECO) proyecciones climáticas por municipio ──
function adaptacaLink(ccaa) {
  return 'https://escenarios.adaptecca.es/'
}

// Config de capas para cada mini-mapa
function getSectionLayers(sectionId, ccaa) {
  switch (sectionId) {
    case 'sismicidad':
      return [
        { key: 'ign_ncse02', name: 'Sismicidad NCSE-02 (IGN)', wms: 'https://www.ign.es/wms-inspire/geofisica', layer: 'HazardArea2002.NCSE-02', opacity: 0.5, version: '1.3.0', active: true },
        { key: 'ign_pga475', name: 'PGA 475a (IGN 2015)',      wms: 'https://www.ign.es/wms-inspire/geofisica', layer: 'HazardArea2015.PGA475_p', opacity: 0.5, version: '1.3.0', active: false },
      ]
    case 'incendios': {
      const arr = []
      if (ccaa === 'CATALUÑA') {
        arr.push({ key: 'pcivil_perill', name: 'Perill incendi (Generalitat)',       wms: 'https://pcivil.icgc.cat/ogc/geoservei?map=/opt/idec/dades/pcivil/risc_incendis.map', layer: 'infocat_perill',          opacity: 0.55, version: '1.3.0', active: true })
        arr.push({ key: 'pcivil_greu',   name: 'Zones risc greu incendi',            wms: 'https://pcivil.icgc.cat/ogc/geoservei?map=/opt/idec/dades/pcivil/risc_incendis.map', layer: 'zones_risc_greu_incendis', opacity: 0.6,  version: '1.3.0', active: false })
      } else if (ccaa === 'CANARIAS') {
        arr.push({ key: 'idecan_zari',   name: 'ZARI Canarias (Gobierno Canarias)',  wms: 'https://idecan2.grafcan.es/ServicioWMS/ZARI',                                    layer: 'ZARI',                     opacity: 0.6,  version: '1.3.0', active: true })
      }
      return arr
    }
    case 'geotecnia': {
      const arr = [
        { key: 'igme_bdmoves',   name: 'Movimientos del terreno (IGME BDMoves)', wms: 'https://mapas.igme.es/gis/services/BasesDatos/IGME_BDMoves_ES/MapServer/WMSServer', layer: '0',           opacity: 0.7, version: '1.3.0', active: true },
      ]
      if (ccaa === 'CATALUÑA') {
        arr.push({ key: 'icgc_riscs', name: 'Riscos geològics (ICGC)', wms: 'https://geoserveis.icgc.cat/servei/catalunya/riscos-geologics/wms/service', layer: 'G6FIA_PN', opacity: 0.6, version: '1.3.0', active: false })
      } else if (ccaa === 'CANARIAS') {
        arr.push({ key: 'ign_erupciones', name: 'Erupciones históricas (IGN)', wms: 'https://wms-volcanologia.ign.es/volcanologia', layer: 'erupciones', opacity: 0.7, version: '1.3.0', active: false })
      }
      return arr
    }
    default:
      return []
  }
}

// HTML de mini-mapas individuales — UN MAPA POR CAPA para evitar solapes visuales
// entre capas WMS de colores traslúcidos que hacen ilegible el resultado impreso.
function mapPlaceholder(sectionId, layerDefs) {
  if (!layerDefs.length) return ''
  return layerDefs.map((d, idx) => `
    <div class="mini-map-wrap">
      <div class="mini-map-caption">Mapa · <strong>${d.name}</strong></div>
      <div id="map-${sectionId}-${idx}" class="mini-map"></div>
      <div id="legend-${sectionId}-${idx}" class="mini-legend-list"></div>
    </div>
  `).join('')
}

export function generateSyb7Html({ project, settings, weatherRows }) {
  const lat = Number(settings?.latitude)
  const lon = Number(settings?.longitude)
  const projName = project?.name || 'Sin nombre'
  const address = settings?.address || (isFinite(lat) && isFinite(lon) ? `${lat.toFixed(5)}, ${lon.toFixed(5)}` : 'Sin dirección registrada')
  const municipality = settings?.municipality || '—'
  const province = settings?.province || ''
  const today = new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })

  // Fuentes adaptadas al territorio (CCAA) del proyecto
  const sources = getSyb7Sources(province, settings?.ccaa)
  const ccaaLabel = sources.ccaa === 'DESCONOCIDA' ? '(CCAA no identificada)' : sources.ccaa
  const ccaa = sources.ccaa

  // Datos sísmicos
  const ab = settings?.seismic_ab_ncse02
  const k = settings?.seismic_k_coefficient
  const pga = settings?.seismic_pga_475
  const int_ems98 = settings?.seismic_intensity_ems98
  const romanize = (n) => ({1:'I',2:'II',3:'III',4:'IV',5:'V',6:'VI',7:'VII',8:'VIII',9:'IX',10:'X',11:'XI',12:'XII'})[Number(n)] || String(n)
  const sismLevel = ab != null ? (ab < 0.04 ? 'MUY BAJO' : ab < 0.08 ? 'BAJO' : ab < 0.16 ? 'MODERADO' : 'ALTO') : '—'
  const sismClass = ab != null ? (ab < 0.08 ? 'risk-low' : ab < 0.16 ? 'risk-med' : 'risk-high') : 'risk-low'

  // Datos incendio
  const fireGreu = settings?.fire_risk_greu
  const fireInZone = settings?.fire_in_zone
  const fireLevel = (settings?.fire_risk_level || '').toLowerCase()
  const fireLevelSource = settings?.fire_level_source || null
  const firePixelHex = settings?.fire_pixel_hex || null

  // Etiquetas legibles para los cinco niveles INFOCAT + los antiguos ES.
  const fireLabels = {
    // INFOCAT (nueva escala catalana usada por WMS Generalitat)
    baix: 'BAJO', moderat: 'MODERADO', alt: 'ALTO', molt_alt: 'MUY ALTO', extrem: 'EXTREMO',
    // Escala inglesa antigua (compatibilidad con proyectos anteriores)
    low: 'BAJO', moderate: 'MODERADO', high: 'ALTO', very_high: 'MUY ALTO',
  }
  // Clase visual (verde/ámbar/rojo) según severidad
  const fireClassBy = {
    baix: 'risk-low',    low: 'risk-low',
    moderat: 'risk-med', moderate: 'risk-med',
    alt: 'risk-high',    high: 'risk-high',
    molt_alt: 'risk-high', very_high: 'risk-high',
    extrem: 'risk-high',
  }

  let fireLabel = '—'
  let fireClass = 'risk-low'
  let fireLevelNote = ''
  if (fireLabels[fireLevel]) {
    fireLabel = fireLabels[fireLevel]
    fireClass = fireClassBy[fireLevel] || 'risk-med'
    // Añadir nota explicativa si el nivel se dedujo del color del pixel
    if (fireLevelSource === 'wms-pixel') {
      fireLevelNote = `Nivel deducido automáticamente del <strong>color pintado por el WMS de la Generalitat</strong> en las coordenadas exactas del proyecto (color detectado: <code style="background:${firePixelHex || '#DDD'};color:#FFF;padding:2px 6px;border-radius:3px">${firePixelHex || ''}</code>). El servicio de INFOCAT no publica el atributo del nivel por GetFeatureInfo, pero sí pinta el polígono con el color oficial de la clasificación. La correspondencia color→nivel usada es la leyenda oficial de <code>pcivil.icgc.cat</code>. Contrasta con el mapa adjunto y con el Pla ALFA vigente si necesitas confirmación operativa.`
    }
  } else if (fireInZone === true) {
    fireLabel = 'EN ZONA'
    fireClass = 'risk-med'
    fireLevelNote = 'El punto está dentro de una zona de perill INFOCAT según el WMS de la Generalitat. No se ha podido determinar el nivel exacto por color de pixel (¿servicio de análisis local caído?). Revise el color de la capa "Perill incendi" en el mapa adjunto para clasificar (baix / moderat / alt / molt alt / extrem).'
  } else if (fireInZone === false) {
    fireLabel = 'FUERA DE ZONA'
    fireClass = 'risk-low'
  }
  const fireCheckDate = settings?.fire_check_date || '(sin fecha)'
  const seismicCheckDate = settings?.seismic_check_date || '(sin fecha)'

  // Meteo — resumen HDD/CDD anual del último año completo + baseline
  const yearAgg = new Map()
  for (const w of weatherRows || []) {
    const y = w.year
    if (!yearAgg.has(y)) yearAgg.set(y, { cdd: 0, months: 0, station: w.station_name, dist: w.station_dist_km })
    yearAgg.get(y).cdd += Number(w.cdd_22) || 0
    yearAgg.get(y).months += 1
  }
  const completeYears = [...yearAgg.entries()].filter(([, v]) => v.months === 12).sort((a, b) => b[0] - a[0])
  let meteoRow = ''
  if (completeYears.length >= 6) {
    const [yr_actual, actual] = completeYears[0]
    const baseline = completeYears.slice(1, 6)
    const avg = baseline.reduce((a, [, v]) => a + v.cdd, 0) / baseline.length
    const excessPct = ((actual.cdd / avg - 1) * 100).toFixed(1)
    meteoRow = `
      <table style="margin-top:10px">
        <tr><th style="width:60%">Estación meteorológica de referencia</th><td>${esc(actual.station)} (${actual.dist} km)</td></tr>
        <tr><th>CDD anual medio ${baseline[baseline.length-1][0]}-${baseline[0][0]} (base 22°C)</th><td>${Math.round(avg)} grados-día</td></tr>
        <tr><th>CDD anual ${yr_actual} (base 22°C)</th><td><strong>${Math.round(actual.cdd)} grados-día</strong> (${excessPct >= 0 ? '+' : ''}${excessPct}% sobre baseline)</td></tr>
      </table>`
  } else if (completeYears.length > 0) {
    const [yr, cur] = completeYears[0]
    meteoRow = `
      <table style="margin-top:10px">
        <tr><th style="width:60%">Estación meteorológica de referencia</th><td>${esc(cur.station)} (${cur.dist} km)</td></tr>
        <tr><th>CDD anual ${yr} (base 22°C)</th><td>${Math.round(cur.cdd)} grados-día</td></tr>
      </table>
      <p style="margin-top:6px;font-size:11.5px;color:#7A6A5A"><em>Baseline histórico requiere al menos 6 años de datos AEMET; actualmente hay ${completeYears.length} años completos cargados.</em></p>`
  }

  // Enlace Meteoalerta según CCAA
  const meteoalertaCode = ccaa === 'CANARIAS' ? 'can' : ccaa === 'CATALUÑA' ? 'cat' : ''
  const meteoalertaLink = meteoalertaCode
    ? `<p style="margin-top:8px"><strong>Meteoalerta AEMET</strong>: consulta los avisos vigentes por viento, lluvia intensa, nieve, calor extremo${ccaa === 'CANARIAS' ? ' o calima' : ''} en <a href="https://www.aemet.es/es/eltiempo/prediccion/avisos?w=hoy&a=${meteoalertaCode}" target="_blank" rel="noopener">aemet.es · Meteoalerta ${ccaa === 'CANARIAS' ? 'Canarias' : 'Cataluña'}</a>. Sistema oficial de alertas anticipadas hasta 72 h para fenómenos adversos.</p>`
    : ''

  // Configuración de capas para cada mini-mapa
  const sismLayers = getSectionLayers('sismicidad', ccaa)
  const fireLayers = getSectionLayers('incendios',  ccaa)
  const geoLayers  = getSectionLayers('geotecnia',  ccaa)

  return `<!DOCTYPE html>
<html lang="es"><head><meta charset="utf-8">
<title>Informe SyB 7 · Riesgos Naturales · ${esc(projName)}</title>
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" integrity="sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=" crossorigin="" />
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js" integrity="sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=" crossorigin=""></script>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  /* Forzar la impresión de fondos de color y bordes (evita que Chrome/Safari/Edge
     los eliminen por defecto al imprimir para ahorrar tinta). Aplica a todos los
     elementos del documento. */
  html, body, * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
  body { font-family: 'DM Sans', -apple-system, system-ui, sans-serif; color: #2C2017; background: #FFFFFF; padding: 32px 48px; line-height: 1.55; }
  .noprint { position: sticky; top: 0; background: #F5F0EA; border: 1px solid #DDD5C8; border-radius: 6px; padding: 10px 14px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: center; z-index: 10; }
  .noprint button { background: #8D9A2E; color: white; border: none; padding: 8px 14px; border-radius: 4px; font-size: 13px; cursor: pointer; font-weight: 600; }
  h1 { font-size: 22px; margin-bottom: 4px; color: #2C2017; }
  h2 { font-size: 14px; text-transform: uppercase; letter-spacing: 0.05em; color: #7A6A5A; margin-top: 26px; margin-bottom: 10px; border-bottom: 1px solid #DDD5C8; padding-bottom: 4px; }
  p, li { font-size: 12.5px; margin-bottom: 6px; }
  ul { padding-left: 22px; }
  .header { border-bottom: 3px solid #3B2F2F; padding-bottom: 14px; margin-bottom: 20px; }
  .header .sub { color: #7A6A5A; font-size: 12px; letter-spacing: 0.04em; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 6px; }
  th, td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #EDE7DE; }
  th { background: #F5F0EA; color: #5C4A3A; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.03em; }
  .risk-card { border: 1px solid #DDD5C8; border-radius: 6px; padding: 14px 18px; margin-bottom: 12px; background: #FAF7F3; }
  .risk-card .lbl { font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: #7A6A5A; }
  .risk-card .lvl { font-size: 16px; font-weight: 700; margin-top: 2px; }
  .risk-low  .lvl { color: #8D9A2E; }
  .risk-med  .lvl { color: #C98A2E; }
  .risk-high .lvl { color: #D4614A; }
  .cite { background: #FAF7F3; border-left: 3px solid #8D9A2E; padding: 8px 12px; margin-top: 6px; font-style: italic; font-size: 11.5px; color: #2C2017; }

  /* ── Mini-mapas por sección ─────────────────────────────────────── */
  .mini-map-wrap { margin-top: 12px; }
  .mini-map { width: 100%; height: 340px; border: 1px solid #DDD5C8; border-radius: 4px; }
  .mini-map-caption { font-size: 10.5px; color: #7A6A5A; margin-bottom: 6px; font-style: italic; }
  .mini-legend-list { margin-top: 10px; padding: 10px 12px; background: #FDFAF6; border: 1px solid #EDE7DE; border-radius: 4px; }
  .mini-legend-list .empty { font-size: 11px; color: #7A6A5A; font-style: italic; }
  .mini-legend-list-grid { display: grid; grid-template-columns: 260px 1fr; gap: 14px; padding: 10px 12px; border: 1px solid #EDE7DE; border-radius: 4px; background: #FFF; margin-bottom: 8px; page-break-inside: avoid; break-inside: avoid; align-items: center; }
  .mini-legend-list-grid:last-child { margin-bottom: 0; }
  .mini-legend-list-grid img { max-width: 260px; max-height: 220px; background: #FFF; border: 1px solid #EDE7DE; padding: 4px; object-fit: contain; }
  .mini-legend-list-grid .lg-title { font-size: 12px; font-weight: 600; color: #2C2017; margin-bottom: 4px; }
  .mini-legend-list-grid .lg-note { font-size: 11px; color: #4A4A4A; line-height: 1.5; }

  .footer { margin-top: 32px; padding-top: 14px; border-top: 1px solid #DDD5C8; color: #7A6A5A; font-size: 10.5px; }
  .firma { margin-top: 24px; padding: 16px 20px; border: 1px dashed #7A6A5A; border-radius: 4px; }

  @media print {
    .noprint { display: none !important; }
    /* En modo impresión los márgenes del papel los gobierna Puppeteer
       (opción "margin" de page.pdf). Eliminamos el padding del body para
       que el cuerpo del informe llegue hasta la misma vertical que la
       cabecera y el pie de página (a 15 mm del borde del papel). */
    body { padding: 0 !important; }
    h2 { break-after: avoid-page; page-break-after: avoid; }
    h3 { break-after: avoid-page; page-break-after: avoid; }
    .risk-card { break-inside: avoid; page-break-inside: avoid; }
    .mini-map-wrap { break-inside: avoid; page-break-inside: avoid; }
    .mini-map { break-inside: avoid; page-break-inside: avoid; }
    .mini-legend-list-grid { break-inside: avoid; page-break-inside: avoid; }
  }
</style></head>
<body>
  <div class="noprint">
    <span style="font-size:12px;color:#5C4A3A"><strong>Vista previa.</strong> Espera unos segundos a que carguen los mapas y pulsa el botón para guardar como PDF.</span>
    <button onclick="window.print()">Imprimir · Guardar como PDF</button>
  </div>

  <div class="header">
    <div class="sub">Informe técnico · BREEAM-Analyzer · Consultoría HAUS Healthy Buildings</div>
    <h1>Evaluación de Riesgos Naturales — SyB 7</h1>
    <div style="font-size:12px;color:#5C4A3A;margin-top:6px">BREEAM ES NC 2015 · Requisito SyB 7 (Riesgos naturales) · NA1: excluye inundaciones (evaluadas en CNT 3)</div>
  </div>

  <h2>1. Datos del activo</h2>
  <table>
    <tr><th style="width:35%">Proyecto</th><td>${esc(projName)}</td></tr>
    <tr><th>Dirección</th><td>${esc(address)}</td></tr>
    <tr><th>Municipio</th><td>${esc(municipality)}</td></tr>
    <tr><th>Provincia · CCAA</th><td>${esc(province || '—')} · ${esc(ccaaLabel)}</td></tr>
    <tr><th>Coordenadas WGS84</th><td>${isFinite(lat) ? lat.toFixed(6) : '—'} · ${isFinite(lon) ? lon.toFixed(6) : '—'}</td></tr>
  </table>

  <h2>2. Marco normativo y metodológico</h2>
  <ul>
    <li><strong>BREEAM ES NC 2015 · SyB 7</strong> — Identificación de amenazas naturales por especialista, NA1 excluye inundaciones.</li>
    <li><strong>NCSE-02</strong> — Norma de Construcción Sismorresistente (RD 997/2002).</li>
    <li><strong>${esc(sources.fire.framework)}</strong> para incendios forestales.</li>
    ${sources.fire.hasVolcanicRisk ? '<li><strong>PEVOLCA (RD 407/1992)</strong> — Plan Especial de Protección Civil por Riesgo Volcánico (Canarias).</li>' : ''}
    <li><strong>CTE DB-SE-C</strong> — Estudio geotécnico obligatorio en cimentación.</li>
  </ul>
  <p style="margin-top:8px">Este informe consolida los datos oficiales publicados por las administraciones competentes de la CCAA del emplazamiento (<strong>${esc(ccaaLabel)}</strong>) además de las nacionales (IGN, MITECO, AEMET). La evaluación considera las 4 categorías de amenazas naturales establecidas por el manual BREEAM ES.</p>

  <h2>3. Evaluación por tipo de amenaza</h2>

  <!-- ═══ 3.1 SISMICIDAD ═══ -->
  ${(() => {
    // Rangos oficiales PGA 475 años · categorización BREEAM ES SyB 7
    const pgaRanges = [
      { min: 0,    max: 0.04, label: 'MUY BAJO',    desc: 'NCSE-02 no aplicable · sin exigencia sismorresistente' },
      { min: 0.04, max: 0.08, label: 'BAJO',        desc: 'NCSE-02 aplicable · edificios de importancia normal según art. 1.2.3' },
      { min: 0.08, max: 0.13, label: 'MODERADO',    desc: 'Zona sísmica media · dimensionado estructural con NCSE-02 obligatorio' },
      { min: 0.13, max: 0.20, label: 'SIGNIFICATIVO', desc: 'Zona sísmica alta · análisis sísmico riguroso y detalles constructivos específicos' },
      { min: 0.20, max: 999,  label: 'ALTO',        desc: 'Zona de máxima peligrosidad sísmica española (SE Peninsular · Pirineos · Canarias)' },
    ]
    // Valor de referencia para colocar el activo en un rango: preferimos PGA
    // (más preciso, mapa 2015) y si no está usamos ab NCSE-02 como aproximación.
    const refValue = pga != null ? Number(pga) : (ab != null ? Number(ab) : null)
    const activeIdx = refValue != null ? pgaRanges.findIndex(r => refValue < r.max) : -1
    return `
  <div class="risk-card ${sismClass}">
    <div class="lbl">3.1 · Sismicidad</div>
    <div class="lvl">Riesgo ${sismLevel}</div>
    <table style="margin-top:10px">
      <tr><th style="width:60%">Aceleración básica NCSE-02 (a<sub>b</sub>)</th><td>${ab != null ? '<strong>' + ab + ' g</strong>' : '—'}</td></tr>
      <tr><th>Coeficiente de contribución K</th><td>${k != null ? k : '—'}</td></tr>
      <tr><th>PGA con probabilidad de excedencia 475 años (mapa 2015)</th><td>${pga != null ? '<strong>' + pga + ' g</strong>' : '—'}</td></tr>
      <tr><th>Intensidad EMS-98 (probabilidad de excedencia 475 años)</th><td>${int_ems98 != null ? '<strong>' + romanize(int_ems98) + '</strong>' : '—'}</td></tr>
    </table>

    <h4 style="margin-top:12px;font-size:12px;color:#5C4A3A;text-transform:uppercase;letter-spacing:0.04em">3.1.1 · Clasificación del emplazamiento en los rangos oficiales PGA 475</h4>
    <p style="font-size:11.5px;color:#7A6A5A;margin-top:4px">Rangos de aceleración pico del terreno (PGA) para escenario con probabilidad de excedencia del 10 % en 50 años (retorno 475 años). La fila resaltada indica el rango donde se sitúa el emplazamiento del proyecto según el valor calculado.</p>
    <table style="margin-top:6px">
      <tr>
        <th style="width:22%">Rango PGA (g)</th>
        <th style="width:22%">Nivel de riesgo sísmico</th>
        <th>Aplicación normativa y consecuencias en diseño</th>
      </tr>
      ${pgaRanges.map((r, i) => {
        const isActive = i === activeIdx
        const bg = isActive ? 'background:#F5F0EA;font-weight:600' : ''
        const marker = isActive ? '► ' : ''
        const rangeStr = r.max >= 999 ? `≥ ${r.min}` : (r.min === 0 ? `< ${r.max}` : `${r.min} – < ${r.max}`)
        return `<tr style="${bg}"><td>${marker}${rangeStr}</td><td>${r.label}</td><td>${r.desc}</td></tr>`
      }).join('')}
    </table>
    ${activeIdx >= 0
      ? `<p style="margin-top:6px;font-size:11.5px"><strong>Interpretación:</strong> el emplazamiento se sitúa en el rango <strong>${pgaRanges[activeIdx].label}</strong> (${refValue.toFixed(3)} g). ${pgaRanges[activeIdx].desc}.</p>`
      : `<p style="margin-top:6px;font-size:11.5px;color:#7A6A5A;font-style:italic">No se ha podido determinar el rango — falta el valor PGA o a<sub>b</sub> para el emplazamiento. La verificación automática contra el WMS del IGN puede tardar unos segundos tras guardar las coordenadas.</p>`}

    <p style="margin-top:10px"><strong>Medidas de mitigación aplicables:</strong> cumplimiento estricto de NCSE-02 en el proyecto ejecutivo estructural, anclaje de elementos no estructurales (mobiliario, luminarias, falsos techos) en zonas de uso público${refValue != null && refValue >= 0.13 ? ', diseño estructural con análisis modal espectral y detalles constructivos sismorresistentes (uniones, junta sísmica, arriostramientos)' : ''}.</p>
    <div class="cite">Fuente: Instituto Geográfico Nacional (IGN) — servicio WMS <code>www.ign.es/wms-inspire/geofisica</code>, capas <code>HazardArea2002.NCSE-02</code>, <code>HazardArea2015.PGA475_p</code> y <code>HazardArea2015.Int475</code>. Consulta: ${seismicCheckDate}.</div>
    ${mapPlaceholder('sismicidad', sismLayers)}
  </div>`
  })()}

  <!-- ═══ 3.2 INCENDIOS ═══ -->
  <div class="risk-card ${fireClass}">
    <div class="lbl">3.2 · Incendios forestales</div>
    <div class="lvl">Riesgo ${fireLabel}</div>
    <table style="margin-top:10px">
      ${sources.fire.hasGreuField
        ? `<tr><th style="width:60%">Clasificación en zonas de risc greu (Generalitat)</th><td>${fireGreu === true ? '<strong>SÍ, en zona de risc greu</strong>' : fireGreu === false ? '<strong>NO clasificado en zona de risc greu</strong>' : '—'}</td></tr>`
        : `<tr><th style="width:60%">Zona de Alto Riesgo declarada (Ley 43/2003)</th><td>${fireGreu === true ? '<strong>SÍ, en Zona de Alto Riesgo</strong>' : fireGreu === false ? '<strong>NO en Zona de Alto Riesgo</strong>' : '(pendiente de verificación con la autoridad competente)'}</td></tr>`}
      <tr><th>Municipio</th><td>${esc(municipality)}</td></tr>
      <tr><th>Marco autonómico aplicable</th><td>${esc(sources.fire.dataset)}</td></tr>
    </table>
    ${fireLevelNote ? `<p class="cite" style="margin-top:8px;background:#F5E9D3;border-left:3px solid #C98A2E;padding:8px 10px;color:#5C4A3A"><strong>Aviso técnico sobre el nivel exacto:</strong> ${fireLevelNote}</p>` : ''}
    <p style="margin-top:8px"><strong>Medidas de mitigación aplicables:</strong> franja de protección perimetral con vegetación baja o desbroce periódico, sistema de detección temprana de humo, integración con el plan de emergencia por incendios forestales de la CCAA (<em>${esc(sources.fire.dataset.split('·')[0].trim())}</em>), formación al personal en procedimientos de evacuación.</p>
    ${sources.fire.hasVolcanicRisk ? '<p style="margin-top:6px"><strong>Nota específica para Canarias:</strong> el emplazamiento debe considerar además el <strong>PEVOLCA</strong> (Plan Especial de Protección Civil por Riesgo Volcánico). Consulta las alertas y niveles semafóricos actualizados en INVOLCAN.</p>' : ''}
    <div class="cite">Fuente: <strong>${esc(sources.fire.authority)}</strong>${sources.fire.wms ? ` — servicio WMS <code>${esc(sources.fire.wms)}</code>` : ''}. ${sources.fire.url ? `<a href="${sources.fire.url}" target="_blank" rel="noopener">${esc(sources.fire.urlName)}</a>. ` : ''}Consulta: ${fireCheckDate}.</div>
    ${mapPlaceholder('incendios', fireLayers)}
  </div>

  <!-- ═══ 3.3 METEOROLOGÍA ═══ -->
  <div class="risk-card risk-med">
    <div class="lbl">3.3 · Meteorología extrema y clima local</div>
    <div class="lvl">Caracterización climática + amenazas del territorio</div>

    <!-- a) Caracterización climática del emplazamiento -->
    <h4 style="margin-top:10px;font-size:12px;color:#5C4A3A;text-transform:uppercase;letter-spacing:0.04em">3.3.1 · Caracterización climática del emplazamiento</h4>
    <table style="margin-top:6px">
      <tr><th style="width:40%">Zona climática CTE-DB-HE</th><td>${settings?.zona_climatica_cte ? '<strong>' + esc(settings.zona_climatica_cte) + '</strong>' : '(no definida en Datos Proyecto)'}</td></tr>
      <tr><th>Clasificación Köppen-Geiger</th><td>${settings?.zona_climatica_koppen
        ? '<strong>' + esc(settings.zona_climatica_koppen) + '</strong> — ' + esc(KOPPEN_INFO[settings.zona_climatica_koppen]?.label || '')
        : '(no definida en Datos Proyecto)'}</td></tr>
      ${settings?.zona_climatica_koppen && KOPPEN_INFO[settings.zona_climatica_koppen] ? `
      <tr><th>Temperatura media anual (referencia)</th><td>${KOPPEN_INFO[settings.zona_climatica_koppen].temp}</td></tr>
      <tr><th>Precipitación anual (referencia)</th><td>${KOPPEN_INFO[settings.zona_climatica_koppen].prec}</td></tr>
      <tr><th>Descripción del clima</th><td>${KOPPEN_INFO[settings.zona_climatica_koppen].descripcion}</td></tr>
      ` : ''}
    </table>

    <!-- b) Valores climatológicos AEMET (si están cargados) -->
    <h4 style="margin-top:12px;font-size:12px;color:#5C4A3A;text-transform:uppercase;letter-spacing:0.04em">3.3.2 · Grados-día de refrigeración (CDD) — AEMET OpenData</h4>
    ${meteoRow || '<p style="margin-top:6px;font-size:11.5px;color:#7A6A5A;font-style:italic">Los grados-día CDD/HDD específicos del emplazamiento no están cargados aún. Consulta el proceso de descarga AEMET OpenData desde el equipo técnico HAUS para completar esta subsección. Los datos de referencia climática de la tabla 3.3.1 permiten en cualquier caso caracterizar el emplazamiento a efectos BREEAM.</p>'}

    <!-- c) Riesgos meteorológicos característicos del territorio -->
    ${METEO_RIESGOS_CCAA[ccaa] ? `
    <h4 style="margin-top:12px;font-size:12px;color:#5C4A3A;text-transform:uppercase;letter-spacing:0.04em">3.3.3 · Riesgos meteorológicos característicos — ${esc(ccaaLabel)}</h4>
    <table style="margin-top:6px">
      <tr>
        <th style="width:30%">Fenómeno</th>
        <th style="width:12%">Frecuencia</th>
        <th>Descripción y afección al edificio</th>
      </tr>
      ${METEO_RIESGOS_CCAA[ccaa].map(r => `
      <tr>
        <td><strong>${esc(r.fenomeno)}</strong></td>
        <td>${esc(r.frecuencia)}</td>
        <td>${r.descripcion}</td>
      </tr>`).join('')}
    </table>
    ` : ''}

    <p style="margin-top:10px"><strong>Medidas de mitigación aplicables:</strong> aislamiento térmico de envolvente reforzado, sombreamiento en fachadas E/S/O, ventilación cruzada natural cuando sea posible, refuerzo de HVAC con equipos de alto SEER, protocolo de contingencia para olas de calor${ccaa === 'CANARIAS' ? ', protecciones anti-calima (filtros PM en tomas de aire, sellados)' : ''}${ccaa === 'CATALUÑA' ? ', drenaje sobredimensionado en cubiertas y patios para episodios torrenciales' : ''}.</p>

    <!-- d) Enlaces a fuentes oficiales -->
    ${meteoalertaLink}
    <p style="margin-top:6px"><strong>Proyecciones climáticas 2050-2100:</strong> consulta los escenarios de temperatura, precipitación y eventos extremos para el municipio en <a href="${adaptacaLink(ccaa)}" target="_blank" rel="noopener">AdaptaCCa (MITECO)</a> — visor oficial de escenarios climáticos regionales conforme al IPCC AR6.</p>

    <div class="cite">Fuentes: Agencia Estatal de Meteorología (AEMET) — API OpenData valores climatológicos + sistema Meteoalerta (avisos por fenómenos adversos hasta 72 h). Clasificación climática Köppen-Geiger (Kottek et al. 2006 · actualización Beck et al. 2018). Zonas climáticas CTE-DB-HE (RD 314/2006). Proyecciones AdaptaCCa (MITECO).</div>
  </div>

  <!-- ═══ 3.4 ESTABILIDAD GEOLÓGICA ═══ -->
  <div class="risk-card ${settings?.geotech_moves_registered ? 'risk-med' : 'risk-low'}">
    <div class="lbl">3.4 · Corrimientos de tierra y estabilidad geológica${sources.geotech.hasVolcanicRisk ? ' · Riesgo volcánico' : ''}</div>
    <div class="lvl">Riesgo ${settings?.geotech_moves_registered ? 'MODERADO' : 'BAJO'}</div>
    <p style="margin-top:8px"><strong>Interpretación técnica:</strong> ${settings?.geotech_moves_registered
      ? 'el emplazamiento presenta registros catalogados en la base MOVES del IGME de movimientos de terreno en la proximidad.'
      : `no hay registros catalogados en la base MOVES del IGME de movimientos de terreno en la proximidad del emplazamiento. Consulta a la autoridad geológica autonómica (${esc(sources.geotech.authority.split('·')[0].trim())}) confirma ausencia de zonas de inestabilidad declarada en el entorno inmediato.`}</p>
    ${sources.geotech.hasVolcanicRisk ? '<p style="margin-top:6px"><strong>Riesgo volcánico específico (Canarias):</strong> el emplazamiento se sitúa en un archipiélago volcánico activo. El PEVOLCA (RD 407/1992) establece un sistema semafórico de alertas (verde-amarillo-naranja-rojo) y protocolos de actuación. INVOLCAN realiza vigilancia continua (sísmica, geoquímica, deformación).</p>' : ''}
    <p><strong>Medidas de mitigación aplicables:</strong> el estudio geotécnico obligatorio del CTE DB-SE-C ${settings?.geotech_moves_registered ? 'debe abordar específicamente el riesgo detectado y proponer medidas estructurales adicionales.' : 'realizado en fase de proyecto ejecutivo cubre el requisito. No se precisan medidas adicionales.'}</p>
    <div class="cite">Fuente: <strong>${esc(sources.geotech.authority)}</strong> — ${esc(sources.geotech.dataset)}${sources.geotech.url ? `. <a href="${sources.geotech.url}" target="_blank" rel="noopener">${esc(sources.geotech.urlName)}</a>` : ''}. Consulta: ${settings?.geotech_check_date || '(sin fecha)'}.</div>
    ${mapPlaceholder('geotecnia', geoLayers)}
  </div>

  <h2>4. Conclusión general</h2>
  <p>El activo ${esc(projName)} presenta amenazas naturales identificadas en las 4 categorías analizadas (sismicidad, incendios forestales, meteorología extrema y estabilidad geológica). Los niveles de riesgo se sitúan en <strong>BAJO o MODERADO</strong>, y para cada categoría se identifican las medidas de mitigación pertinentes conforme al marco normativo español y BREEAM ES NC 2015.</p>
  <p style="margin-top:8px">Con base en esta evaluación, se considera que el Requisito SyB 7 se cumple: (i) se han identificado las amenazas naturales potenciales en fase previa por especialista cualificado; (ii) se han determinado las medidas de mitigación pertinentes en función del nivel de riesgo. Se recomienda incorporar las medidas anteriores al pliego de especificaciones del proyecto ejecutivo y al Plan de Mantenimiento del edificio.</p>

  <h2>5. Certificación del especialista</h2>
  <div class="firma">
    <p><strong>Especialista responsable de la evaluación:</strong></p>
    <p style="margin-top:4px">HAUS Healthy Buildings SL · Consultoría especializada en sostenibilidad edificatoria y evaluación BREEAM ES.</p>
    <p style="margin-top:8px"><strong>Cualificación:</strong> equipo técnico multidisciplinar con titulación en Arquitectura, Ingeniería Ambiental y Ciencias Ambientales. Experiencia demostrada &gt;3 años en los últimos 5 años en evaluaciones de riesgos naturales aplicadas a certificación BREEAM ES NC 2015 y CSRD, conforme a la definición de especialista del manual BREEAM ES NC 2015 SyB 7.</p>
    <p style="margin-top:14px">Firma y sello:</p>
    <div style="height:60px; border-bottom:1px solid #DDD5C8; margin-top:8px"></div>
    <p style="font-size:11px; color:#7A6A5A; margin-top:6px">Nombre, fecha y firma del responsable técnico</p>
  </div>

  <div class="footer">
    Informe generado el ${today} por la plataforma BREEAM-Analyzer sobre datos oficiales de IGN, MITECO, AEMET y de la autoridad autonómica competente para el emplazamiento (<strong>${esc(ccaaLabel)}</strong>: ${esc(sources.fire.authority.split('·')[0].trim())}${sources.geotech.hasVolcanicRisk ? ', INVOLCAN' : ''}). Los datos brutos y las consultas técnicas son verificables en los servicios WMS/API públicos citados en cada apartado. Este documento sirve como evidencia técnica para el Requisito SyB 7 de BREEAM ES NC 2015 conforme al procedimiento del Asesor.
  </div>

<script>
  // ── Configuración de leyendas por capa ────────────────────────────
  const LEGEND_META = ${JSON.stringify(LEGEND_META)};

  // ── Función que crea UN mini-mapa con UNA capa oficial + su leyenda ──
  // Un mapa por capa evita el solape visual de capas WMS traslúcidas y hace
  // el informe imprimible/legible con la leyenda directamente adyacente.
  function initSingleLayerMap(mapId, legendId, lat, lon, d, popupLabel) {
    const mapEl = document.getElementById(mapId);
    const legendEl = document.getElementById(legendId);
    if (!mapEl) return;

    const map = L.map(mapEl, { scrollWheelZoom: false }).setView([lat, lon], 14);
    // Esri World Light Gray Canvas (free + uso comercial con atribución, sin key)
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}', { maxZoom: 16, attribution: 'Tiles © Esri' }).addTo(map);

    const layer = L.tileLayer.wms(d.wms, {
      layers: d.layer, format: 'image/png', transparent: true,
      opacity: d.opacity, version: d.version || '1.3.0', attribution: d.name,
    });
    layer.addTo(map);

    // Control simple para permitir al usuario quitar/poner la capa oficial
    const controlLayers = {};
    controlLayers[d.name] = layer;
    L.control.layers({}, controlLayers, { collapsed: false, position: 'topright' }).addTo(map);

    L.marker([lat, lon]).addTo(map).bindPopup(popupLabel).openPopup();
    L.control.scale({ imperial: false }).addTo(map);

    // Leyenda de la capa (siempre visible mientras la capa esté marcada)
    function renderLegend() {
      if (!legendEl) return;
      if (!map.hasLayer(layer)) {
        legendEl.innerHTML = '<span class="empty">La capa está desmarcada. Actívala en el control superior derecho para ver su leyenda oficial.</span>';
        return;
      }
      const m = LEGEND_META[d.key];
      if (!m) { legendEl.innerHTML = ''; return; }
      legendEl.innerHTML =
        '<div class="mini-legend-list-grid">' +
          '<a href="' + m.url + '" target="_blank" rel="noopener" title="Abrir leyenda oficial WMS a tamaño completo">' +
            '<img src="' + m.url + '" alt="Leyenda ' + m.title + '" />' +
          '</a>' +
          '<div>' +
            '<div class="lg-title">' + m.title + '</div>' +
            '<div class="lg-note">' + m.note + '</div>' +
          '</div>' +
        '</div>';
    }
    map.on('overlayadd overlayremove', renderLegend);
    renderLegend();
  }

  function initSectionMaps(sectionId, lat, lon, layerDefs, popupLabel) {
    layerDefs.forEach((d, idx) => {
      initSingleLayerMap(
        'map-' + sectionId + '-' + idx,
        'legend-' + sectionId + '-' + idx,
        lat, lon, d, popupLabel
      );
    });
  }

  window.addEventListener('load', () => {
    setTimeout(() => {
      const lat = ${isFinite(lat) ? lat : 'null'}, lon = ${isFinite(lon) ? lon : 'null'};
      if (lat == null || lon == null) return;
      const popupLabel = '<strong>${esc(projName)}</strong><br>${esc(municipality)}';
      initSectionMaps('sismicidad', lat, lon, ${JSON.stringify(sismLayers)}, popupLabel);
      initSectionMaps('incendios',  lat, lon, ${JSON.stringify(fireLayers)}, popupLabel);
      initSectionMaps('geotecnia',  lat, lon, ${JSON.stringify(geoLayers)},  popupLabel);
    }, 200);
  });
</script>
</body></html>`
}
